/**
 * Task 245 — follow-up chips under a settled answer.
 *
 * Before this, an answer was a dead end: the reader finished it and either
 * invented another question or left. Every chip here rides a connection Koresh
 * himself affirms (see related-passages.ts), so the suggestion is his, not ours.
 *
 * Chips SUBMIT a question rather than navigate. That keeps the reader in the
 * conversation, gives the follow-up its own citations, and means the component
 * is safe inside the host-page iframe (embed mode), where a navigation would
 * replace the frame and strand them.
 */
import type { Strings } from "../strings";
import type { RelatedPassage } from "../chat/related-passages";

export interface RelatedPassagesProps {
  passages: RelatedPassage[];
  strings: Strings;
  onPick: (question: string) => void;
}

export function RelatedPassages(props: RelatedPassagesProps): JSX.Element | null {
  const { passages, strings, onPick } = props;
  if (passages.length === 0) return null;

  return (
    <nav className="related" aria-label={strings.relatedLabel}>
      <p className="related__lead">{strings.relatedLead}</p>
      <div className="related__chips">
        {passages.map((p) => {
          // The artifact's accented name when present; the capitalised slug
          // only as a fallback for an older artifact.
          const label =
            p.bookName !== undefined
              ? `${p.bookName} ${p.chapter}`
              : strings.citationBookChapter(p.book, p.chapter);
          const fromLabel = p.from === undefined ? null :
            p.from.bookName !== undefined ? `${p.from.bookName} ${p.from.chapter}` :
              strings.citationBookChapter(p.from.book, p.from.chapter);
          return (
            <button
              key={p.key}
              type="button"
              className="related__chip"
              onClick={() => onPick(strings.relatedQuestion(fromLabel, label))}
            >
              <span className="related__chip-text">{label}</span>
              <svg
                className="related__chip-arrow"
                width="13"
                height="13"
                viewBox="0 0 13 13"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M2.6 6.5h7M6.6 3.5l3 3-3 3"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
